import { useState, useMemo, useCallback } from 'react'
import { useLibraryStore, type UploadingFile } from '@/stores/library-store'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import {
  DOCUMENT_CATEGORY_LABELS,
  DOCUMENT_DISPLAY_STATUS_CONFIG,
  getDocumentDisplayStatus,
  formatFileSize,
} from '@/lib/schemas'
import { FileTypeIcon } from '@/components/ui/file-type-icon'
import {
  MoreVertical,
  Download,
  Trash,
  FolderInput,
  Tag,
  FileText,
  Loader2,
  AlertCircle,
  X,
  SquarePen,
  Shield,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  CalendarDays,
  Archive,
  ArchiveRestore,
  UploadCloud,
  History,
} from 'lucide-react'
import { toast } from 'sonner'
import { LibraryOverview } from '@/components/LibraryOverview'
import { MoveDocumentsDialog } from '@/components/MoveDocumentsDialog'
import { RenameFolderDialog } from '@/components/RenameFolderDialog'
import { ChangeFolderCategoryDialog } from '@/components/ChangeFolderCategoryDialog'
import { DeleteFolderDialog } from '@/components/DeleteFolderDialog'
import { DeleteDocumentsDialog } from '@/components/DeleteDocumentsDialog'
import { TagDocumentsDialog } from '@/components/TagDocumentsDialog'
import { ReplaceDocumentDialog } from '@/components/ReplaceDocumentDialog'
import { EditDatesDialog } from '@/components/EditDatesDialog'
import { DocumentVersionsDialog } from '@/components/DocumentVersionsDialog'
import { getExpiryStatus } from '@/lib/document-expiry'
import type { LibraryDocument } from '@/lib/schemas'
import type { useDropZoneUpload } from '@/hooks/useDropZoneUpload'
import { DropZoneOverlay } from '@/components/DropZoneOverlay'
import { usePreviewStore } from '@/stores/preview-store'
import { useAuthStore } from '@/stores/auth-store'

type SortColumn = 'name' | 'size' | 'date'
type SortDirection = 'asc' | 'desc'

interface DocumentPanelProps {
  /** Shared dropzone instance from LibraryPage (lifted so toolbar + panel share state). */
  dropzone: ReturnType<typeof useDropZoneUpload>
}

export function DocumentPanel({ dropzone }: DocumentPanelProps) {
  const {
    selectedFolderId,
    selectedDocumentIds,
    folders,
    getFilteredDocuments,
    getFolderPath,
    selectFolder,
    selectDocument,
    selectAllDocuments,
    clearDocumentSelection,
    uploadingFiles,
    removeUploadingFile,
    searchQuery,
    isSearchPending,
    setSearchQuery,
    hasActiveFilters,
    clearAllFilters,
    getDownloadUrl,
    renameDocument,
    setFactBank,
    setFactBankBulk,
    setQualityTier,
    factBankFilter,
    loadMoreDocuments,
    isLoadingMore,
  } = useLibraryStore()

  // Raw loaded-window count vs server total drive the "Load more" affordance.
  // (Kept separate from the client-side filtered `documents` list below.)
  const loadedCount = useLibraryStore((s) => s.documents.length)
  const documentsTotal = useLibraryStore((s) => s.documentsTotal)
  const hasMoreDocuments = loadedCount < documentsTotal

  // Auth context for permission gating
  const currentUserId = useAuthStore((s) => s.user?.id)
  const isAdmin = useAuthStore((s) => s.isPlatformAdmin || s.role === 'admin')
  const isViewer = useAuthStore((s) => s.role === 'viewer')

  // RBAC split: curation (rename/move/tag/dates/archive/replace) is any writing
  // member; destruction (delete) stays uploader-or-admin. F25 moved replace into
  // curation — it retains + attributes the predecessor, and gating it on
  // ownership left admin-less orgs unable to supersede anything.
  const canCurate = !isViewer
  const canDelete = useCallback(
    (doc: { uploadedBy: string }) => isAdmin || doc.uploadedBy === currentUserId,
    [isAdmin, currentUserId]
  )

  // Download state
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null)

  const handleDownload = useCallback(async (documentId: string) => {
    setDownloadingDocId(documentId)
    try {
      const url = await getDownloadUrl(documentId)
      window.open(url, '_blank')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to download document'
      toast.error(message)
    } finally {
      setDownloadingDocId(null)
    }
  }, [getDownloadUrl])

  // Move dialog state
  const [moveDialogOpen, setMoveDialogOpen] = useState(false)
  const [moveDialogDocIds, setMoveDialogDocIds] = useState<string[]>([])

  // Tag dialog state
  const [tagDialogOpen, setTagDialogOpen] = useState(false)
  const [tagDialogDocIds, setTagDialogDocIds] = useState<string[]>([])

  // Folder edit/delete dialog state
  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // Document delete dialog state
  const [deleteDocsDialogOpen, setDeleteDocsDialogOpen] = useState(false)
  const [deleteDocsIds, setDeleteDocsIds] = useState<string[]>([])

  // Replace / edit-dates dialog state (single-doc, versioning)
  const [replaceDialogDoc, setReplaceDialogDoc] = useState<LibraryDocument | null>(null)
  const [editDatesDialogDoc, setEditDatesDialogDoc] = useState<LibraryDocument | null>(null)
  const [versionsDialogDoc, setVersionsDialogDoc] = useState<LibraryDocument | null>(null)

  const handleToggleArchive = useCallback(async (doc: LibraryDocument) => {
    const isArchived = doc.qualityTier === 'archive'
    try {
      await setQualityTier(doc.id, isArchived ? null : 'archive')
      toast.success(isArchived ? 'Document unarchived' : 'Document archived')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update archive status'
      toast.error(message)
    }
  }, [setQualityTier])

  // Drag-and-drop upload — shared instance lifted to LibraryPage.
  // The hidden file input lives on LibraryPage (so toolbar Upload works even
  // when this panel is unmounted); here we only need the drag-target props.
  const {
    getRootProps,
    isDragActive,
    isDisabled: isDropDisabled,
    uploadErrors,
    clearErrors,
  } = dropzone

  // Column sorting state (local — resets on folder change)
  const [sortColumn, setSortColumn] = useState<SortColumn>('date')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  const handleSort = useCallback((column: SortColumn) => {
    setSortColumn((prev) => {
      if (prev === column) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
        return prev
      }
      setSortDirection(column === 'name' ? 'asc' : 'desc')
      return column
    })
  }, [])

  // Document rename state
  const [renamingDocId, setRenamingDocId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [isRenamingSaving, setIsRenamingSaving] = useState(false)

  const handleRenameSubmit = useCallback(async (documentId: string, name: string) => {
    const trimmed = name.trim()
    if (!trimmed) {
      setRenamingDocId(null)
      return
    }
    setIsRenamingSaving(true)
    try {
      await renameDocument(documentId, trimmed)
      toast.success('Document renamed')
      setRenamingDocId(null)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to rename document'
      toast.error(message)
    } finally {
      setIsRenamingSaving(false)
    }
  }, [renameDocument])

  const handleRenameCancel = useCallback(() => {
    if (!isRenamingSaving) {
      setRenamingDocId(null)
    }
  }, [isRenamingSaving])

  // When searching, search ALL documents; otherwise show selected folder
  const isSearching = searchQuery.trim().length > 0
  const filtersActive = hasActiveFilters()

  const documents = isSearching
    ? getFilteredDocuments(null)
    : getFilteredDocuments(selectedFolderId)

  const sortedDocuments = useMemo(() => {
    const sorted = [...documents].sort((a, b) => {
      switch (sortColumn) {
        case 'name':
          return a.name.localeCompare(b.name)
        case 'size':
          return a.size - b.size
        case 'date':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      }
    })
    return sortDirection === 'desc' ? sorted.reverse() : sorted
  }, [documents, sortColumn, sortDirection])

  const selectedFolder = selectedFolderId
    ? folders.find((f) => f.id === selectedFolderId)
    : null
  const folderPath = selectedFolderId ? getFolderPath(selectedFolderId) : []

  // Check if current user can delete all selected documents (for bulk delete)
  const canDeleteAllSelected = useMemo(() => {
    if (isAdmin) return true
    return Array.from(selectedDocumentIds).every((id) => {
      const doc = documents.find((d) => d.id === id)
      return doc && doc.uploadedBy === currentUserId
    })
  }, [selectedDocumentIds, documents, isAdmin, currentUserId])

  // Bulk Fact Bank toggle direction: remove only when every selected doc is
  // already a member; otherwise the action adds the whole selection.
  const allSelectedInFactBank = useMemo(() => {
    if (selectedDocumentIds.size === 0) return false
    return Array.from(selectedDocumentIds).every(
      (id) => documents.find((d) => d.id === id)?.inFactBank === true
    )
  }, [selectedDocumentIds, documents])

  const handleBulkFactBank = useCallback(async () => {
    const ids = Array.from(selectedDocumentIds)
    const adding = !allSelectedInFactBank
    try {
      await setFactBankBulk(ids, adding)
      toast.success(
        adding
          ? `Added ${ids.length} document${ids.length === 1 ? '' : 's'} to the Fact Bank`
          : `Removed ${ids.length} document${ids.length === 1 ? '' : 's'} from the Fact Bank`
      )
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update Fact Bank'
      toast.error(message)
    }
  }, [selectedDocumentIds, allSelectedInFactBank, setFactBankBulk])

  // Show library overview when no folder selected, not searching, and no filter
  // is active. An active filter (Fact Bank lens, expiring lens, category/tags)
  // switches the null-folder view from the overview to the filtered list.
  if (selectedFolderId === null && !isSearching && !filtersActive) {
    return <LibraryOverview onUploadClick={dropzone.open} />
  }

  const allSelected = documents.length > 0 && documents.every((d) => selectedDocumentIds.has(d.id))
  const someSelected = documents.some((d) => selectedDocumentIds.has(d.id))

  const handleSelectAll = () => {
    if (allSelected) {
      clearDocumentSelection()
    } else {
      selectAllDocuments(true)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  // Short day + month (no year) for the "Updated {d MMM}" date-cell label.
  const formatDateShort = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
    })
  }

  return (
    <div className="relative flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-6 py-3 border-b">
        <div className="flex items-center justify-between">
          <div>
            {/* Breadcrumb or Search Results */}
            {isSearching ? (
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-medium">Search Results</h2>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => setSearchQuery('')}
                    title="Clear search"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
                <p className="text-meta">
                  Showing results across all folders for &ldquo;{searchQuery.trim()}&rdquo;
                </p>
              </div>
            ) : folderPath.length > 0 ? (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-1">
                {folderPath.map((folder, idx) => {
                  const isLast = idx === folderPath.length - 1
                  return (
                    <span key={folder.id} className="flex items-center gap-1.5">
                      {idx > 0 && <ChevronRight className="h-3.5 w-3.5 text-subtle" />}
                      {isLast ? (
                        <span className="text-foreground font-medium">{folder.name}</span>
                      ) : (
                        <button
                          className="hover:underline cursor-pointer text-muted-foreground"
                          onClick={() => selectFolder(folder.id)}
                        >
                          {folder.name}
                        </button>
                      )}
                    </span>
                  )
                })}
                {selectedFolder && !isViewer && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-6 w-6 ml-1">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onClick={() => setRenameDialogOpen(true)}>
                        <SquarePen className="h-4 w-4 mr-2" />
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setCategoryDialogOpen(true)}>
                        <Tag className="h-4 w-4 mr-2" />
                        Change Category
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setDeleteDialogOpen(true)}
                        className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            ) : null}

            {/* Folder info */}
            <div className="flex items-center gap-2">
              {selectedFolder && !isSearching && (
                <Badge
                  variant="outline"
                  className="text-xs"
                >
                  {DOCUMENT_CATEGORY_LABELS[selectedFolder.category]}
                </Badge>
              )}
              {/* IMP-1 — during the debounce+RTT window the rows below still
                  answer the previous term, so don't claim a count for the term
                  now in the box. */}
              <span className="text-meta" aria-live="polite">
                {isSearchPending ? (
                  'Searching…'
                ) : (
                  <>
                    {documents.length} document{documents.length !== 1 ? 's' : ''}
                    {isSearching && ` matching "${searchQuery.trim()}"`}
                  </>
                )}
              </span>
            </div>
          </div>

        </div>
      </div>

      {/* Upload Error Banner */}
      {uploadErrors.length > 0 && (
        <div className="flex items-center gap-2 border-b bg-destructive/10 px-6 py-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0 text-destructive" />
          <span className="flex-1 text-sm text-destructive">
            {uploadErrors.length} file(s) failed: {uploadErrors.map((e) => e.fileName).join(', ')}
          </span>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={clearErrors}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* Document List with Dropzone */}
      <div className="relative flex flex-1 flex-col overflow-hidden">
        <ScrollArea className="flex-1">
          <div {...getRootProps()} className="min-h-full relative">
            {/* Hidden file input lives at the LibraryPage level so dropzone.open()
                works from the toolbar even when this panel isn't mounted. */}

            {/* Drop Overlay */}
            {isDragActive && (
              <DropZoneOverlay
                folderName={selectedFolder?.name ?? null}
                disabled={isDropDisabled}
              />
            )}

            {/* Empty states and document list. IMP-1 — an empty window while a
                search is still in flight is not a "no results" answer yet, so
                the empty state waits for the refetch to land. */}
            {documents.length === 0 && !isSearchPending ? (
              <DocumentListEmptyState
                isSearching={isSearching}
                filtersActive={filtersActive}
                factBankLensOn={factBankFilter}
                onClearFilters={clearAllFilters}
              />
            ) : (
          <div
            className={cn(
              'px-6 py-4 transition-opacity',
              // Stale rows stay legible but visibly not-current, rather than
              // being presented as results for the term just typed.
              isSearchPending && 'opacity-40 pointer-events-none'
            )}
            role="table"
            aria-label="Documents"
            aria-busy={isSearchPending}
          >
            {/* Table Header */}
            <div role="row" className="flex items-center gap-3 px-3 py-2 text-xs font-medium text-muted-foreground border-b">
              <Checkbox
                checked={allSelected}
                onCheckedChange={handleSelectAll}
                aria-label="Select all"
                className={cn(!someSelected && !allSelected && 'opacity-50')}
              />
              <SortableColumnHeader
                column="name"
                label="Name"
                className="flex-1"
                activeColumn={sortColumn}
                direction={sortDirection}
                onSort={handleSort}
              />
              <div role="columnheader" className="w-28">Uploaded by</div>
              <div role="columnheader" className="w-24">Status</div>
              <SortableColumnHeader
                column="size"
                label="Size"
                className="w-16 justify-end"
                activeColumn={sortColumn}
                direction={sortDirection}
                onSort={handleSort}
              />
              <SortableColumnHeader
                column="date"
                label="Date"
                className="w-24 justify-end"
                activeColumn={sortColumn}
                direction={sortDirection}
                onSort={handleSort}
              />
              <div className="w-14" />
            </div>

            {/* Uploading Files */}
            {uploadingFiles.filter((f) => f.folderId === selectedFolderId).length > 0 && (
              <div className="space-y-0.5 mb-2">
                {uploadingFiles
                  .filter((f) => f.folderId === selectedFolderId)
                  .map((file) => (
                    <UploadingFileItem
                      key={file.id}
                      file={file}
                      onRemove={() => removeUploadingFile(file.id)}
                    />
                  ))}
              </div>
            )}

            {/* Document Rows */}
            <div role="rowgroup" className="space-y-0.5">
              {sortedDocuments.map((doc) => {
                const isSelected = selectedDocumentIds.has(doc.id)
                const displayStatus = getDocumentDisplayStatus(doc)
                const processingConfig = DOCUMENT_DISPLAY_STATUS_CONFIG[displayStatus]
                const expiryStatus = getExpiryStatus(doc, new Date())
                const version = doc.version ?? 1
                const isArchived = doc.qualityTier === 'archive'
                const canDeleteDoc = canDelete(doc)
                // Fact Bank shield tooltip becomes attribution: who vouched for
                // the doc and when ("You" for the current user). Users mark the
                // Fact Bank; a member with no recorded curator (the retired
                // auto-add, or a deleted account) gets no attribution at all.
                const factBankCurator =
                  doc.factBankSetBy == null
                    ? null
                    : doc.factBankSetBy === currentUserId
                      ? 'You'
                      : (doc.factBankSetByName ?? 'a former member')
                const factBankTitle = !doc.inFactBank
                  ? 'Add to Fact Bank'
                  : factBankCurator == null
                    ? 'In Fact Bank'
                    : `In Fact Bank — Added by ${factBankCurator}${
                        doc.factBankSetAt ? ` · ${formatDate(doc.factBankSetAt)}` : ''
                      }`

                return (
                  <div
                    role="row"
                    key={doc.id}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-md transition-colors group',
                      'hover:bg-accent/50',
                      isSelected && 'bg-accent'
                    )}
                  >
                    {/* Checkbox */}
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={(checked) => selectDocument(doc.id, !!checked)}
                      aria-label={`Select ${doc.name}`}
                    />

                    {/* Icon & Name */}
                    <div role="cell" className="flex-1 flex items-center gap-2 min-w-0">
                      <FileTypeIcon mimeType={doc.mimeType} size={20} />
                      <div className="min-w-0 flex-1">
                        {renamingDocId === doc.id ? (
                          <Input
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                void handleRenameSubmit(doc.id, renameValue)
                              } else if (e.key === 'Escape') {
                                handleRenameCancel()
                              }
                            }}
                            onBlur={handleRenameCancel}
                            disabled={isRenamingSaving}
                            autoFocus
                            className="h-7 text-sm"
                          />
                        ) : (
                          <>
                            {/* F28 — the filename owns this line outright. Badges
                                used to sit inline here and took their width off
                                the name, collapsing it to "Skywav…" — seven
                                characters, identical across every Skywave doc in
                                the folder. They now wrap to the metadata line. */}
                            <div className="flex min-w-0">
                              <button
                                className="text-sm font-medium truncate text-left hover:text-[hsl(var(--brand))] transition-colors min-w-0"
                                title={doc.name}
                                onClick={() =>
                                  void usePreviewStore.getState().openPreview({
                                    documentId: doc.id,
                                    documentName: doc.name,
                                    context: 'library',
                                  })
                                }
                              >
                                {doc.name}
                              </button>
                            </div>
                            {(version > 1 ||
                              expiryStatus !== null ||
                              doc.tags.length > 0 ||
                              doc.extractedMetadata?.['pastBid'] != null) && (
                              <div className="flex flex-wrap items-center gap-1 mt-0.5">
                                {/* Version chip — neutral, only after a replace (version > 1). */}
                                {version > 1 && (
                                  <Badge variant="secondary" dot={false} className="shrink-0 text-[10px] px-1 py-0 tabular-nums">
                                    v{version}
                                  </Badge>
                                )}
                                {/* Expiry pill — expired is hard-excluded from AI retrieval. */}
                                {expiryStatus === 'expired' && (
                                  <Badge variant="destructive" className="shrink-0 text-[10px]">
                                    Expired
                                  </Badge>
                                )}
                                {expiryStatus === 'expiring-soon' && (
                                  <Badge variant="warning" className="shrink-0 text-[10px]">
                                    Expires soon
                                  </Badge>
                                )}
                                {doc.extractedMetadata?.['pastBid'] != null && (() => {
                                  // Past-bid outcome pill (Specs/win-loss-harvest-spec.md §3) —
                                  // keyed on harvest provenance, which the list response carries
                                  const outcome = (doc.extractedMetadata['pastBid'] as { outcome?: string | null }).outcome
                                  return (
                                    <Badge
                                      variant={outcome === 'won' ? 'success' : outcome === 'lost' ? 'destructive' : 'secondary'}
                                      className="text-[10px] px-1 py-0"
                                    >
                                      {outcome === 'won' ? 'Won' : outcome === 'lost' ? 'Lost' : 'Awaiting outcome'}
                                    </Badge>
                                  )
                                })()}
                                {doc.tags.slice(0, 3).map((tag) => (
                                  <Badge
                                    key={tag.id}
                                    variant="secondary"
                                    className="text-[10px] px-1 py-0"
                                  >
                                    {tag.name}
                                  </Badge>
                                ))}
                                {doc.tags.length > 3 && (
                                  <span className="text-[10px] text-muted-foreground">
                                    +{doc.tags.length - 3}
                                  </span>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Uploaded by */}
                    <div role="cell" className="w-28 text-meta truncate" title={doc.uploaderName ?? undefined}>
                      {doc.uploaderName ?? '-'}
                    </div>

                    {/* Status */}
                    <div role="cell" className="w-24 flex items-center gap-1">
                      <Badge
                        variant={processingConfig.variant}
                        dot={displayStatus === 'processing' ? false : undefined}
                        className="text-[10px]"
                      >
                        {displayStatus === 'processing' && (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        )}
                        {processingConfig.label}
                      </Badge>
                    </div>

                    {/* Size */}
                    <div role="cell" className="w-16 text-meta text-right">
                      {formatFileSize(doc.size)}
                    </div>

                    {/* Date — "Updated {d MMM}" (foreground) when the doc has
                        been modified since creation; plain created date otherwise. */}
                    {(() => {
                      const isUpdated =
                        !!doc.updatedAt &&
                        new Date(doc.updatedAt).getTime() - new Date(doc.createdAt).getTime() > 1000
                      const title = isUpdated
                        ? `Created ${formatDate(doc.createdAt)} · Updated ${formatDate(doc.updatedAt!)}`
                        : `Created ${formatDate(doc.createdAt)}`
                      return (
                        <div
                          role="cell"
                          className="w-24 text-meta text-right tabular-nums"
                          title={title}
                        >
                          {isUpdated ? (
                            <span className="text-foreground">Updated {formatDateShort(doc.updatedAt!)}</span>
                          ) : (
                            formatDate(doc.createdAt)
                          )}
                        </div>
                      )
                    })()}

                    {/* Actions — Fact Bank shield (persistent for members,
                        hover-reveal otherwise; same pattern as LibraryOverview)
                        + kebab menu */}
                    <div className="w-14 flex items-center justify-end gap-0.5">
                      {!isViewer && (
                        <button
                          type="button"
                          onClick={() => void setFactBank(doc.id, !doc.inFactBank)}
                          title={factBankTitle}
                          aria-label={doc.inFactBank ? 'Remove from Fact Bank' : 'Add to Fact Bank'}
                          className={cn(
                            'h-7 w-7 inline-flex items-center justify-center rounded-md',
                            'hover:bg-accent transition-colors',
                            doc.inFactBank
                              ? 'text-[hsl(var(--brand))]'
                              : 'opacity-0 group-hover:opacity-100 text-muted-foreground'
                          )}
                        >
                          <Shield
                            className={cn('h-3.5 w-3.5', doc.inFactBank && 'fill-[hsl(var(--brand))]')}
                          />
                        </button>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="More actions"
                            className="h-7 w-7 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 transition-opacity"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            disabled={downloadingDocId === doc.id}
                            onClick={() => void handleDownload(doc.id)}
                          >
                            {downloadingDocId === doc.id ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4 mr-2" />
                            )}
                            {downloadingDocId === doc.id ? 'Downloading...' : 'Download'}
                          </DropdownMenuItem>
                          {/* F26 — a labelled history list replaces the old
                              "Download previous version", which named neither
                              the version nor its date and only ever reached the
                              newest predecessor. Reading history is not a
                              privileged act, so it sits with Download. */}
                          {version > 1 && (
                            <DropdownMenuItem onClick={() => setVersionsDialogDoc(doc)}>
                              <History className="h-4 w-4 mr-2" />
                              Version history…
                            </DropdownMenuItem>
                          )}
                          {!isViewer && (
                            <DropdownMenuItem
                              onClick={() => void setFactBank(doc.id, !doc.inFactBank)}
                            >
                              <Shield className={cn('h-4 w-4 mr-2', doc.inFactBank && 'fill-[hsl(var(--brand))] text-[hsl(var(--brand))]')} />
                              {doc.inFactBank ? 'Remove from Fact Bank' : 'Add to Fact Bank'}
                            </DropdownMenuItem>
                          )}
                          {/* Curation tier — any member (rename/move/tag/dates/archive). */}
                          {canCurate && (
                            <>
                              <DropdownMenuItem
                                onClick={() => {
                                  setRenamingDocId(doc.id)
                                  setRenameValue(doc.name)
                                }}
                              >
                                <SquarePen className="h-4 w-4 mr-2" />
                                Rename
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setMoveDialogDocIds([doc.id])
                                  setMoveDialogOpen(true)
                                }}
                              >
                                <FolderInput className="h-4 w-4 mr-2" />
                                Move to...
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setTagDialogDocIds([doc.id])
                                  setTagDialogOpen(true)
                                }}
                              >
                                <Tag className="h-4 w-4 mr-2" />
                                Add tag
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setEditDatesDialogDoc(doc)}>
                                <CalendarDays className="h-4 w-4 mr-2" />
                                Edit dates…
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => void handleToggleArchive(doc)}>
                                {isArchived ? (
                                  <ArchiveRestore className="h-4 w-4 mr-2" />
                                ) : (
                                  <Archive className="h-4 w-4 mr-2" />
                                )}
                                {isArchived ? 'Unarchive' : 'Archive'}
                              </DropdownMenuItem>
                              {/* F25 — versioning is curation, not destruction:
                                  any writing member may supersede any document
                                  (the predecessor is retained + attributed). */}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => setReplaceDialogDoc(doc)}>
                                <UploadCloud className="h-4 w-4 mr-2" />
                                Upload new version
                              </DropdownMenuItem>
                            </>
                          )}
                          {/* Destruction tier — uploader or admin (delete only). */}
                          {canDeleteDoc && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => {
                                  setDeleteDocsIds([doc.id])
                                  setDeleteDocsDialogOpen(true)
                                }}
                                className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                              >
                                <Trash className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Load more — the list is server-paginated (100/page). Shown only
                while the loaded window is short of the server total. */}
            {hasMoreDocuments && (
              <div className="flex justify-center pt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-sm text-muted-foreground"
                  onClick={() => void loadMoreDocuments()}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isLoadingMore ? 'Loading…' : `Load more (${loadedCount} of ${documentsTotal})`}
                </Button>
              </div>
            )}
          </div>
        )}
          </div>
        </ScrollArea>
      </div>

      {/* Bulk Actions Snackbar — floating, slides up from the bottom of the
          document panel when selection is non-empty. */}
      {selectedDocumentIds.size > 0 && (
        <div
          data-testid="bulk-actions-snackbar"
          className={cn(
            'absolute bottom-4 left-1/2 -translate-x-1/2 z-20',
            'flex items-center gap-2 px-3 py-2',
            'rounded-lg shadow-md border border-foreground/10',
            'bg-foreground text-background',
            'animate-in slide-in-from-bottom-2 fade-in duration-150'
          )}
          role="status"
          aria-live="polite"
        >
          <span className="text-sm font-medium px-1 tabular-nums">
            {selectedDocumentIds.size} selected
          </span>
          {!isViewer && (
            <>
              <div className="h-4 w-px bg-background/20 mx-1" aria-hidden />
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2.5 text-background hover:bg-background/10 hover:text-background"
                onClick={() => void handleBulkFactBank()}
              >
                <Shield className="h-4 w-4 mr-1.5" />
                {allSelectedInFactBank ? 'Remove from Fact Bank' : 'Add to Fact Bank'}
              </Button>
            </>
          )}
          {/* Curation bulk actions — any member (Move / Tag). */}
          {!isViewer && (
            <>
              <div className="h-4 w-px bg-background/20 mx-1" aria-hidden />
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2.5 text-background hover:bg-background/10 hover:text-background"
                onClick={() => {
                  setMoveDialogDocIds(Array.from(selectedDocumentIds))
                  setMoveDialogOpen(true)
                }}
              >
                <FolderInput className="h-4 w-4 mr-1.5" />
                Move
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2.5 text-background hover:bg-background/10 hover:text-background"
                onClick={() => {
                  setTagDialogDocIds(Array.from(selectedDocumentIds))
                  setTagDialogOpen(true)
                }}
              >
                <Tag className="h-4 w-4 mr-1.5" />
                Tag
              </Button>
              {/* Delete stays uploader-or-admin; disabled (not hidden) on a
                  mixed-ownership selection so the gate is legible. The wrapping
                  span carries the title so the tooltip is reachable on hover —
                  the disabled button has `pointer-events-none` and never gets it. */}
              <span
                className="inline-flex"
                title={!canDeleteAllSelected ? 'Only uploaders or admins can delete' : undefined}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!canDeleteAllSelected}
                  className="h-8 px-2.5 text-background hover:bg-background/10 hover:text-background disabled:opacity-40"
                  onClick={() => {
                    setDeleteDocsIds(Array.from(selectedDocumentIds))
                    setDeleteDocsDialogOpen(true)
                  }}
                >
                  <Trash className="h-4 w-4 mr-1.5" />
                  Delete
                </Button>
              </span>
            </>
          )}
          <div className="h-4 w-px bg-background/20 mx-1" aria-hidden />
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-background hover:bg-background/10 hover:text-background"
            onClick={clearDocumentSelection}
            aria-label="Clear selection"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Move Documents Dialog */}
      <MoveDocumentsDialog
        open={moveDialogOpen}
        onOpenChange={setMoveDialogOpen}
        documentIds={moveDialogDocIds}
      />

      {/* Tag Documents Dialog */}
      <TagDocumentsDialog
        open={tagDialogOpen}
        onOpenChange={setTagDialogOpen}
        documentIds={tagDialogDocIds}
      />

      {/* Folder Edit/Delete Dialogs */}
      {selectedFolder && (
        <>
          <RenameFolderDialog
            folder={selectedFolder}
            open={renameDialogOpen}
            onOpenChange={setRenameDialogOpen}
          />
          <ChangeFolderCategoryDialog
            folder={selectedFolder}
            open={categoryDialogOpen}
            onOpenChange={setCategoryDialogOpen}
          />
          <DeleteFolderDialog
            folder={selectedFolder}
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
          />
        </>
      )}

      {/* Delete Documents Dialog */}
      <DeleteDocumentsDialog
        documentIds={deleteDocsIds}
        open={deleteDocsDialogOpen}
        onOpenChange={setDeleteDocsDialogOpen}
      />

      {/* Replace (upload new version) Dialog — single doc, versioning */}
      {replaceDialogDoc && (
        <ReplaceDocumentDialog
          document={replaceDialogDoc}
          open={replaceDialogDoc !== null}
          onOpenChange={(open) => !open && setReplaceDialogDoc(null)}
        />
      )}

      {/* Edit Dates Dialog — single doc, curation tier */}
      {editDatesDialogDoc && (
        <EditDatesDialog
          document={editDatesDialogDoc}
          open={editDatesDialogDoc !== null}
          onOpenChange={(open) => !open && setEditDatesDialogDoc(null)}
        />
      )}

      {/* Version History Dialog — read-only, any member (F26) */}
      {versionsDialogDoc && (
        <DocumentVersionsDialog
          document={versionsDialogDoc}
          open={versionsDialogDoc !== null}
          onOpenChange={(open) => !open && setVersionsDialogDoc(null)}
        />
      )}
    </div>
  )
}

// ===================
// Document List Empty State
// ===================

function DocumentListEmptyState({
  isSearching,
  filtersActive,
  factBankLensOn,
  onClearFilters,
}: {
  isSearching: boolean
  filtersActive: boolean
  factBankLensOn: boolean
  onClearFilters: () => void
}) {
  // Fact Bank lens first — an empty bank needs explanation (what it is, how
  // to add to it), not the generic "no matches" copy.
  if (factBankLensOn) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Shield className="h-8 w-8 text-muted-foreground mb-4" />
        <h3 className="text-sm font-medium mb-1">Your Fact Bank is empty here</h3>
        <p className="text-sm text-muted-foreground max-w-sm mb-4">
          The Fact Bank holds your authoritative company documents — insurance
          certificates, accreditations, policies. The AI can answer factual
          questions only from them. Hover a document and click the shield to
          add it.
        </p>
        <Button variant="ghost" size="sm" onClick={onClearFilters}>
          Clear filters
        </Button>
      </div>
    )
  }

  // Filters take precedence over search/folder copy — they're the most
  // actionable thing (one click clears them) so we surface that path first.
  if (filtersActive) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <FileText className="h-8 w-8 text-muted-foreground mb-4" />
        <h3 className="text-sm font-medium mb-1">No documents match these filters</h3>
        <p className="text-sm text-muted-foreground max-w-sm mb-4">
          Try removing one or more filters to widen the results.
        </p>
        <Button variant="ghost" size="sm" onClick={onClearFilters}>
          Clear filters
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <FileText className="h-8 w-8 text-muted-foreground mb-4" />
      <h3 className="text-sm font-medium mb-1">
        {isSearching ? 'No matching documents' : 'No documents'}
      </h3>
      <p className="text-sm text-muted-foreground max-w-sm">
        {isSearching
          ? 'Nothing in your library matches that, in a file name or inside a document. Try a different term.'
          : 'Upload past proposals, case studies, and company policies. The AI uses these as evidence when drafting bid responses.'}
      </p>
    </div>
  )
}

// ===================
// Sortable Column Header Component
// ===================

interface SortableColumnHeaderProps {
  column: SortColumn
  label: string
  className?: string
  activeColumn: SortColumn
  direction: SortDirection
  onSort: (column: SortColumn) => void
}

function SortableColumnHeader({ column, label, className, activeColumn, direction, onSort }: SortableColumnHeaderProps) {
  const isActive = activeColumn === column
  const SortIcon = isActive && direction === 'asc' ? ChevronUp : ChevronDown

  return (
    <button
      role="columnheader"
      className={cn(
        'flex items-center gap-0.5 hover:text-foreground transition-colors',
        isActive ? 'text-foreground' : 'text-muted-foreground',
        className
      )}
      onClick={() => onSort(column)}
    >
      {label}
      <SortIcon className={cn('h-3 w-3', !isActive && 'opacity-0')} />
    </button>
  )
}

// ===================
// Uploading File Item Component
// ===================

interface UploadingFileItemProps {
  file: UploadingFile
  onRemove: () => void
}

function UploadingFileItem({ file, onRemove }: UploadingFileItemProps) {
  const isError = file.status === 'error'
  const isUploading = file.status === 'uploading' || file.status === 'pending'

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-md transition-colors',
        isError
          ? 'bg-destructive/5 border border-destructive/20'
          : 'bg-muted/30'
      )}
    >
      {/* Spacer for checkbox alignment */}
      <div className="w-4" />

      {/* Icon & Name */}
      <div className="flex-1 flex items-center gap-2 min-w-0">
        <FileTypeIcon mimeType={file.file.type} size={20} />
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{file.file.name}</p>
          <p className="text-meta">
            {formatFileSize(file.file.size)}
            {isError && file.error && (
              <span className="text-destructive ml-2">{file.error}</span>
            )}
          </p>
        </div>
      </div>

      {/* Uploaded by placeholder */}
      <div className="w-28" />

      {/* Status */}
      <div className="w-24 flex items-center gap-1">
        {isUploading && (
          <Badge variant="warning" dot={false} className="text-[10px]">
            <Loader2 className="h-3 w-3 animate-spin" />
            {file.progress > 0 ? `${file.progress}%` : 'Uploading'}
          </Badge>
        )}
        {isError && (
          <Badge variant="destructive" className="text-[10px]">
            Failed
          </Badge>
        )}
      </div>

      {/* Size placeholder */}
      <div className="w-16" />

      {/* Date placeholder */}
      <div className="w-24" />

      {/* Actions - remove button for errors */}
      <div className="w-14 flex justify-end">
        {isError && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={onRemove}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  )
}
